import { useEffect, useMemo, useState } from 'react';
import type { QnaItem, QnaSort } from '@/services/qnaService';
import { createQna, listQna, markQnaAnswered, deleteQna, toggleQnaHighlight, voteQna } from '@/services/qnaService';
import { useAuth } from '@/context/AuthContext';

export function QnaPanel({ quizId, mode }: { quizId: string; mode: 'audience' | 'host' }) {
  const { user, authReady } = useAuth();
  const [question, setQuestion] = useState('');
  const [items, setItems] = useState<QnaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<QnaSort>('top');

  const canPost = useMemo(() => question.trim().length > 0, [question]);

  async function refresh() {
    setLoading(true);
    try {
      setItems(await listQna(quizId, sort));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!quizId) return;
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizId, sort]);

  async function onPost() {
    if (!quizId || !user?.uid || !canPost) return;
    await createQna(quizId, user.uid, question);
    setQuestion('');
    await refresh();
  }

  const isHost = mode === 'host';

  return (
    <div className="border rounded-lg p-3 bg-white">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="font-semibold">Audience Q&amp;A</div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-600">Sort</label>
          <select className="border rounded px-2 py-1 text-xs" value={sort} onChange={e => setSort(e.target.value as any)}>
            <option value="top">Top</option>
            <option value="newest">Newest</option>
            <option value="unanswered">Unanswered</option>
          </select>
        </div>
      </div>

      {!authReady ? (
        <div className="text-sm text-gray-600">Loading…</div>
      ) : !user?.uid ? (
        <div className="text-sm text-gray-600">Sign in to post questions.</div>
      ) : (
        <div className="flex gap-2 mb-3">
          <input
            className="flex-1 border rounded px-3 py-2 text-sm"
            placeholder="Ask a question…"
            value={question}
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') void onPost();
            }}
          />
          <button
            type="button"
            className="px-3 py-2 rounded bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50"
            disabled={!canPost}
            onClick={() => void onPost()}
          >
            Ask
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-600">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-gray-600">No questions yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map(q => (
            <div key={q.id} className={`border rounded p-2 ${q.highlighted ? 'border-yellow-400 bg-yellow-50' : 'bg-white'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-medium">{q.question}</div>
                <div className="shrink-0 flex items-center gap-2">
                  <button
                    type="button"
                    className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
                    onClick={() => user?.uid && voteQna(quizId, q.id, user.uid, 1).then(refresh)}
                    title="Upvote"
                  >
                    ▲ {q.votes ?? 0}
                  </button>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                {q.answered && <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">Answered</span>}
                {isHost && (
                  <>
                    <button
                      type="button"
                      className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
                      onClick={() => void markQnaAnswered(quizId, q.id, !q.answered).then(refresh)}
                    >
                      {q.answered ? 'Unmark' : 'Mark answered'}
                    </button>
                    <button
                      type="button"
                      className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
                      onClick={() => void toggleQnaHighlight(quizId, q.id).then(refresh)}
                    >
                      {q.highlighted ? 'Unhighlight' : 'Highlight'}
                    </button>
                    <button
                      type="button"
                      className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200"
                      onClick={() => void deleteQna(quizId, q.id).then(refresh)}
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
